/**
 * Admin → Click Cap Rules
 *
 * Manage hierarchical click + sale-amount cap rules. A rule applies to
 * any combination of (publisher × network × brand) — leaving a dimension
 * empty (`Any`) makes it match every value on that axis. Most-specific
 * rule wins at enforcement time.
 */
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, TrendUp, Trash, PencilSimple, Sliders } from "@phosphor-icons/react";

const NETWORKS = [
  "impact", "skimlinks", "flexoffers", "mrge", "partnerize",
  "cj", "admedia", "pubadhub", "pubadhub_kevin", "shopnomix_kevin", "siteplug", "maxbounty", "cashbackhive",
];

export default function AdminClickCapRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pubs, setPubs] = useState([]);
  const [brands, setBrands] = useState([]);
  const [modal, setModal] = useState(null); // { mode: "create" | "edit", rule? }
  const didFetch = useRef(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p, b] = await Promise.all([
        API.get("/admin/click-cap-rules"),
        API.get("/admin/publishers?page_size=500"),
        API.get("/admin/brands?page_size=500"),
      ]);
      setRules(r.data?.rules || []);
      const pubsList = p.data?.publishers || p.data?.data || p.data || [];
      setPubs(Array.isArray(pubsList) ? pubsList : []);
      const brandList = b.data?.brands || b.data?.data || b.data || [];
      setBrands(Array.isArray(brandList) ? brandList : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    fetchAll();
  }, [fetchAll]);

  const remove = async (id) => {
    if (!window.confirm("Delete this rule? Pubs in scope will fall back to the next-most-specific rule or the legacy brand default.")) return;
    try {
      await API.delete(`/admin/click-cap-rules/${id}`);
      toast.success("Rule deleted");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const increment = async (id, defaultPct) => {
    const raw = window.prompt(
      `Increment by what %? (default ${defaultPct || 10})\n\n` +
      `Bumps click_cap and sale_cap by the given %. ` +
      `Publisher will be emailed a transparency note.`,
      String(defaultPct || 10),
    );
    if (raw === null) return;
    const pct = parseFloat(raw);
    if (Number.isNaN(pct) || pct === 0) {
      toast.error("Invalid pct");
      return;
    }
    try {
      const { data } = await API.post(`/admin/click-cap-rules/${id}/increment`, { pct });
      toast.success(
        `Bumped +${pct}% · new click_cap=${data.click_cap_per_day?.toLocaleString?.()} · ` +
        `new sale_cap=$${(data.sale_cap_per_day_usd || 0).toLocaleString?.(undefined, { minimumFractionDigits: 2 })}`
      );
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Increment failed");
    }
  };

  const specificityLabel = (s) => {
    if (s >= 7) return "Pub × Network × Brand";
    if (s === 6) return "Pub × Network";
    if (s === 5) return "Pub × Brand";
    if (s === 4) return "Pub (all)";
    if (s === 3) return "Network × Brand";
    if (s === 2) return "Network (all)";
    if (s === 1) return "Brand (all)";
    return "Global";
  };

  return (
    <div className="space-y-6" data-testid="admin-click-cap-rules">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sliders size={26} weight="duotone" className="text-indigo-700" />
            Click + Sale Cap Rules
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Hierarchical daily caps. Any combination of <span className="font-mono">publisher × network × brand</span> can carry a click-count cap and a sale-amount cap.
            Most-specific rule wins. Set <span className="font-mono">-1</span> for &quot;unlimited&quot;.
            Caps auto-grow by a configurable weekly % when enabled. Original = admin baseline; Current = after bumps.
          </p>
        </div>
        <Button onClick={() => setModal({ mode: "create" })} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ccr-new-btn">
          <Plus size={14} className="mr-1.5" />
          New rule
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Scope</th>
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Publisher</th>
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Network</th>
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Brand</th>
              <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Original</th>
              <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Current</th>
              <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Sale cap</th>
              <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Weekly +%</th>
              <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Last bump</th>
              <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && rules.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-400">No rules yet. Click <b>New rule</b> to author the first one.</td></tr>
            )}
            {rules.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`ccr-row-${r.id}`}>
                <td className="px-4 py-2.5">
                  <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100">
                    {specificityLabel(r.specificity)}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-slate-700 text-xs">{r.publisher_email || <span className="text-slate-300">Any</span>}</td>
                <td className="px-4 py-2.5 text-slate-700 text-xs">{r.network || <span className="text-slate-300">Any</span>}</td>
                <td className="px-4 py-2.5 text-slate-700 text-xs">{r.brand_name || (r.brand_id ? r.brand_id : <span className="text-slate-300">Any</span>)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-slate-500">
                  {r.original_click_cap_per_day === -1 || r.original_click_cap_per_day == null
                    ? <span className="text-emerald-600">∞</span>
                    : Number(r.original_click_cap_per_day).toLocaleString()}
                </td>
                <td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-800">
                  {r.click_cap_per_day === -1 ? <span className="text-emerald-600">∞</span> : (r.click_cap_per_day || 0).toLocaleString()}
                </td>
                <td className="px-4 py-2.5 text-right font-mono">
                  {r.sale_cap_per_day_usd === -1 ? <span className="text-emerald-600">∞</span> : `$${(r.sale_cap_per_day_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
                </td>
                <td className="px-4 py-2.5 text-right font-mono text-slate-600">
                  {r.auto_increment_enabled
                    ? `${r.auto_increment_pct}%`
                    : <span className="text-slate-400">Off</span>}
                </td>
                <td className="px-4 py-2.5 text-right text-xs text-slate-500">{r.last_increment_at ? new Date(r.last_increment_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => increment(r.id, r.auto_increment_pct)} title="Bump now" data-testid={`ccr-increment-${r.id}`}>
                      <TrendUp size={14} weight="duotone" className="text-emerald-700" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setModal({ mode: "edit", rule: r })} title="Edit" data-testid={`ccr-edit-${r.id}`}>
                      <PencilSimple size={14} weight="duotone" className="text-slate-700" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(r.id)} title="Delete" data-testid={`ccr-delete-${r.id}`}>
                      <Trash size={14} weight="duotone" className="text-rose-700" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <RuleModal
          mode={modal.mode}
          rule={modal.rule}
          pubs={pubs}
          brands={brands}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); fetchAll(); }}
        />
      )}
    </div>
  );
}

function RuleModal({ mode, rule, pubs, brands, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [pid, setPid] = useState(rule?.publisher_id || "");
  const [network, setNetwork] = useState(rule?.network || "");
  const [brandId, setBrandId] = useState(rule?.brand_id || "");
  const [clickCap, setClickCap] = useState(rule?.click_cap_per_day ?? -1);
  const [saleCap, setSaleCap] = useState(rule?.sale_cap_per_day_usd ?? -1);
  const [incEnabled, setIncEnabled] = useState(
    rule?.auto_increment_enabled ?? (Number(rule?.auto_increment_pct) > 0 || rule == null)
  );
  const [incPct, setIncPct] = useState(
    rule?.auto_increment_pct > 0 ? rule.auto_increment_pct : 10
  );
  const [saving, setSaving] = useState(false);

  // Brand options filtered by network if selected
  const brandOptions = useMemo(() => {
    if (!network) return brands;
    return brands.filter((b) => (b.network || "").toLowerCase() === network);
  }, [brands, network]);

  const submit = async () => {
    if (Number(clickCap) === -1 && Number(saleCap) === -1) {
      toast.error("Set at least one cap (click_cap or sale_cap). Use -1 for unlimited on the OTHER one only.");
      return;
    }
    setSaving(true);
    try {
      const incFields = {
        auto_increment_enabled: Boolean(incEnabled),
        auto_increment_pct: incEnabled ? Number(incPct) : 0,
      };
      const payload = isEdit
        ? {
            click_cap_per_day: Number(clickCap),
            sale_cap_per_day_usd: Number(saleCap),
            ...incFields,
          }
        : {
            publisher_id: pid || null,
            network: network || null,
            brand_id: brandId || null,
            click_cap_per_day: Number(clickCap),
            sale_cap_per_day_usd: Number(saleCap),
            ...incFields,
          };
      if (isEdit) {
        await API.put(`/admin/click-cap-rules/${rule.id}`, payload);
        toast.success("Rule updated");
      } else {
        await API.post("/admin/click-cap-rules", payload);
        toast.success("Rule created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="ccr-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit rule" : "New click + sale cap rule"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!isEdit && (
            <>
              <div>
                <Label className="text-xs">Publisher (leave blank = all pubs)</Label>
                <select
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                  value={pid}
                  onChange={(e) => setPid(e.target.value)}
                  data-testid="ccr-publisher-select"
                >
                  <option value="">— Any publisher —</option>
                  {pubs.map((u) => (
                    <option key={u.id || u._id} value={u.id || u._id}>
                      {u.email}{u.sub_id ? ` · ${u.sub_id}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Network (leave blank = all networks)</Label>
                <Select value={network || "__any"} onValueChange={(v) => { setNetwork(v === "__any" ? "" : v); setBrandId(""); }}>
                  <SelectTrigger data-testid="ccr-network-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any">— Any network —</SelectItem>
                    {NETWORKS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Brand (leave blank = all brands{network ? ` on ${network}` : ""})</Label>
                <select
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                  value={brandId}
                  onChange={(e) => setBrandId(e.target.value)}
                  data-testid="ccr-brand-select"
                >
                  <option value="">— Any brand —</option>
                  {brandOptions.slice(0, 500).map((b) => (
                    <option key={b.campaign_id} value={b.campaign_id}>
                      {b.name} {b.network ? `(${b.network})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">
                Click cap / day <span className="text-slate-400 font-normal">(-1 = unlimited)</span>
              </Label>
              <Input type="number" value={clickCap} onChange={(e) => setClickCap(e.target.value)} data-testid="ccr-click-cap" />
              {isEdit && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Saving resets original + current to this value (restart growth from here).
                  {rule?.original_click_cap_per_day != null && rule.original_click_cap_per_day !== rule.click_cap_per_day && (
                    <> Original was {Number(rule.original_click_cap_per_day).toLocaleString()}.</>
                  )}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">Sale cap / day USD <span className="text-slate-400 font-normal">(-1 = unlimited)</span></Label>
              <Input type="number" step="0.01" value={saleCap} onChange={(e) => setSaleCap(e.target.value)} data-testid="ccr-sale-cap" />
            </div>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 space-y-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-800 cursor-pointer select-none">
              <input
                type="checkbox"
                className="rounded border-slate-300"
                checked={incEnabled}
                onChange={(e) => setIncEnabled(e.target.checked)}
                data-testid="ccr-inc-enabled"
              />
              Enable weekly auto-increment
            </label>
            {incEnabled && (
              <div>
                <Label className="text-xs">Increment every week (%)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min={0}
                  value={incPct}
                  onChange={(e) => setIncPct(e.target.value)}
                  data-testid="ccr-inc-pct"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Current cap grows by this % once a week. Original baseline stays fixed until you reset.
                </p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ccr-save-btn">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Create rule")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
